import React, { useState, useEffect, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { AppSettings } from "../../config/app-settings.js";
import useAuthStore from "../../stores/auth-store.js";
import OrdersDataTable from "../../components/datatables/orders-datatables.jsx";
import {
  useEvacuationEvents,
  useEvacuationOrders,
} from "../../hooks/useEvacuation.js";

// ==================== UTILITY FUNCTIONS ====================

const convertSecondsToISODateTime = (dateString, timeSeconds) => {
  if (!dateString || timeSeconds === null || timeSeconds === undefined) {
    return null;
  }

  const baseDate = new Date(`${dateString}T00:00:00.000Z`);
  baseDate.setUTCSeconds(baseDate.getUTCSeconds() + parseFloat(timeSeconds));
  return baseDate.toISOString().substring(0, 19);
};

// Transform raw order data to table format
const transformOrderData = (orders) => {
  if (!orders || orders.length === 0) return [];

  return orders.map((order) => {
    // Determine order status
    let orderStatus;
    if (order.order_details.Is_Committed && !order.order_details.Is_Rescinded) {
      orderStatus = 'Active';
    } else if (order.order_details.Is_Rescinded) {
      orderStatus = 'Rescinded';
    } else {
      orderStatus = 'Not Committed';
    }

    // Create datetime from date and time
    let commitDatetime = convertSecondsToISODateTime(
      order.order_details.Commit_Order_Date,
      order.order_details.Commit_Order_Time
    );
    let rescindedDatetime = convertSecondsToISODateTime(
      order.order_details.Rescind_Order_Date,
      order.order_details.Rescind_Order_Time
    );

    return {
      id: order.order_details.ID,
      orderId: order.order_details.ID,
      zoneId: order.zone_id,
      eventId: order.event_id,
      eventStatus: order.event_status,
      eventName: order.order_details.Event_Title,
      eventType: order.order_details.Event_Type_Name,
      createdAt: order.order_details.Created_At,
      createdBy: `${order.order_details.Event_Official_FName} ${order.order_details.Event_Official_LName}`,
      commitDatetime: commitDatetime,
      rescindedDatetime: rescindedDatetime,
      musterStation: `${order.evacuation_data.Dest_Address}`,
      musterStationName: order.evacuation_data.Dest_Name,
      zoneName: order.evacuation_data.Dest_Community,
      affectedAddresses: order.address_count,
      totalEvacVeh: order.evacuation_data.total_evac_veh,
      totalDwellings: order.evacuation_data.total_occupied_dwell,
      totalPeople: order.evacuation_data.total_people,
      orderCommitted: order.order_details.Is_Committed,
      orderRescinded: order.order_details?.Is_Rescinded,
      commitTimestamp: order.order_details.Commit_Timestamp,
      rescindTimestamp: order.order_details.Rescind_Timestamp,
      status: orderStatus,
      lastUpdated: order.order_details.Updated_At || order.order_details.Created_At,
      commitOfficialFirstName: order.order_details.Commit_Official_FName || '-',
      commitOfficialLastName: order.order_details.Commit_Official_LName || '-',
      commitComments: order.order_details.Commit_Comments || 'No comments provided',
      rescindOfficialFirstName: order.order_details.Rescind_Official_FName || '-',
      rescindOfficialLastName: order.order_details.Rescind_Official_LName || '-',
      rescindComments: order.order_details.Rescind_Comments || 'No comments provided',
    };
  });
};

// Transform events data for dropdown
const transformEventsData = (events) => {
  if (!events || events.length === 0) return [];

  return events.map((event) => ({
    id: event.ID,
    eventName: event.Title,
    eventType: event.Event_Type_Name,
    status: event.Status,
  }));
};

// ==================== COMPONENT ====================

function EvacuationOrder() {
  const context = useContext(AppSettings);
  const location = useLocation();
  const userId = useAuthStore((state) => state.user?.id || 0);

  // ==================== LOCAL STATE ====================
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedEventId, setSelectedEventId] = useState("");

  // ==================== REACT QUERY HOOKS ====================
  // Step 1: Fetch events first
  const {
    data: eventsResponse,
    isLoading: isLoadingEvents,
    isSuccess: eventsSuccess, // Track when events load successfully
  } = useEvacuationEvents(userId);

  // Step 2: Fetch orders ONLY after events are loaded
  const {
    data: ordersResponse,
    isLoading: isLoadingOrders,
    error: ordersError
  } = useEvacuationOrders(
    userId,
    selectedEventId ? { eventId: selectedEventId } : {},
    eventsSuccess // Pass dependency - only fetch when events are ready
  );

  // ==================== DERIVED STATE ====================
  const events = transformEventsData(eventsResponse?.events || []);
  const orders = transformOrderData(ordersResponse?.orders || []);
  const isLoading = isLoadingEvents || isLoadingOrders;

  // ==================== HANDLERS ====================
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleEventFilterChange = (e) => {
    setSelectedEventId(e.target.value);
  };

  // ==================== EFFECTS ====================

  // Get eventId from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const eventIdFromUrl = params.get('eventId');
    if (eventIdFromUrl) {
      setSelectedEventId(eventIdFromUrl);
    }
  }, [location.search]);

  // Set page layout
  useEffect(() => {
    context.handleSetAppContentClass('');
    context.handleSetAppSidebarNone(true);
    context.handleSetAppSidebarTwo(false);
    context.handleSetAppSidebarWide(false);
    context.handleSetAppTopMenu(true);

    return () => {
      context.handleSetAppSidebarNone(false);
      context.handleSetAppSidebarTwo(false);
      context.handleSetAppSidebarWide(false);
      context.handleSetAppTopMenu(false);
    };
  }, []);

  // ==================== RENDER ====================
  return (
    <div className="row gx-0">
      <div className="col-12">
        {/* Title */}
        <div className="d-flex align-items-center mb-3">
          <div>
            <h1 className="page-header mb-0">
              Evacuation Orders Table
              <small className="ms-2 text-muted">
                Zone-specific evacuation orders and management
              </small>
            </h1>
          </div>
          <div className="ms-auto">
            <Link
              to="/evacuation-portal-panels/evacuation-portal"
              className="btn btn-primary"
            >
              <i className="fa fa-plus me-2"></i>
              Create Order
            </Link>
          </div>
        </div>

        <div className="panel panel-inverse">
          {/* Header */}
          <div className="panel-heading">
            <h4 className="panel-title">
              <i className="fa fa-map-marker me-2"></i>
              Order History
            </h4>
            <div className="panel-heading-btn">
              <button
                className="btn btn-xs btn-icon btn-default"
                onClick={() => window.location.reload()}
                title="Refresh"
              >
                <i className="fa fa-refresh"></i>
              </button>
            </div>
          </div>

          <div className="panel-body">
            {isLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                {/* ✅ Show loading stage */}
                <div className="ms-3">
                  <p className="mb-0">
                    {isLoadingEvents && 'Loading events...'}
                    {!isLoadingEvents && isLoadingOrders && 'Loading orders...'}
                  </p>
                </div>
              </div>
            ) : ordersError ? (
              <div className="alert alert-danger" role="alert">
                <i className="fa fa-exclamation-triangle me-2"></i>
                Error loading orders: {ordersError.message}
              </div>
            ) : orders.length === 0 && !selectedEventId ? (
              <div className="alert alert-info text-center" role="alert">
                <i className="fa fa-info-circle fa-2x mb-3"></i>
                <h4>No Orders Found</h4>
                <p>No evacuation orders have been created yet.</p>
              </div>
            ) : (
              <div>
                {/* Filters */}
                <div className="row justify-content-end mb-3">
                  <div className="col-md-4">
                    <label htmlFor="eventFilter" className="form-label fw-bold">
                      <i className="fa fa-calendar me-2"></i>
                      Filter by Event:
                    </label>
                    <select
                      id="eventFilter"
                      className="form-select"
                      value={selectedEventId}
                      onChange={handleEventFilterChange}
                    >
                      <option value="">All Events</option>
                      {events.map(event => (
                        <option key={event.id} value={event.id}>
                          {event.eventName} ({event.status})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="statusFilter" className="form-label fw-bold">
                      <i className="bi bi-funnel-fill"></i>
                      Filter by Status:
                    </label>
                    <select
                      id="statusFilter"
                      className="form-select"
                      value={statusFilter}
                      onChange={handleStatusFilterChange}
                    >
                      <option value="">All</option>
                      <option value="Active">Active</option>
                      <option value="Rescinded">Rescinded</option>
                    </select>
                  </div>
                </div>

                {/* Show message if event selected but no orders */}
                {orders.length === 0 && selectedEventId ? (
                  <div className="alert alert-warning text-center" role="alert">
                    <i className="fa fa-exclamation-triangle fa-2x mb-3"></i>
                    <h5>No Orders Found for Selected Event</h5>
                    <p>This event does not have any evacuation orders yet.</p>
                  </div>
                ) : (
                  <OrdersDataTable data={orders} statusFilter={statusFilter} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EvacuationOrder;